import type { EquipmentSlot } from '../equipment';
import {
  PLAYER_LEVEL_MAX,
  certificationForItemLevel,
  type GuildRank,
} from '../progression/guild';

export type EquipmentRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type EquipmentQualityStars = 1 | 2 | 3 | 4 | 5;
export type FundamentalStat = 'VIT' | 'ESP' | 'END' | 'FOR' | 'DEX' | 'INT' | 'SAG' | 'CHA' | 'AGI';
export type ElementId = 'fire' | 'ice' | 'lightning' | 'earth' | 'wind' | 'holy' | 'dark' | 'water' | 'nature';
export type EquipmentAffixKey = FundamentalStat | `affinity_${ElementId}` | `resistance_${ElementId}`;
export type EquipmentDropKind =
  | 'dungeon_monster'
  | 'dungeon_elite'
  | 'dungeon_master'
  | 'dungeon_chest'
  | 'legacy'
  | 'civic';

export interface EquipmentAffixRoll {
  key: EquipmentAffixKey;
  value: number;
}

export interface EquipmentSource {
  kind: EquipmentDropKind;
  dungeonId?: string;
  runId?: string;
  floorIndex?: number;
  receiptId?: string;
}

export interface EquipmentTemplate {
  id: string;
  name: string;
  slot: EquipmentSlot;
  family: string;
  emoji?: string;
  img?: string;
  twoHanded?: boolean;
  ambidextrous?: boolean;
  shieldOnly?: boolean;
  /** Sous-ensemble optionnel. Sans valeur, le pool sûr du slot est utilisé. */
  allowedAffixes?: EquipmentAffixKey[];
}

export interface EquipmentInstance {
  instanceId: string;
  templateId: string;
  ownerId: string;
  slot: EquipmentSlot;
  itemLevel: number;
  rarity: EquipmentRarity;
  qualityStars: EquipmentQualityStars;
  affixes: EquipmentAffixRoll[];
  requiredCertification: GuildRank;
  bound: true;
  source: EquipmentSource;
  generatedAt: number;
  legacy?: boolean;
  /** Bonus historiques conservés uniquement pour migrer sans perte les objets fixes. */
  legacyStats?: Record<string, number>;
}

export interface GenerateEquipmentInput {
  instanceId: string;
  ownerId: string;
  template: EquipmentTemplate;
  itemLevel: number;
  rarity: EquipmentRarity;
  source: EquipmentSource;
  generatedAt?: number;
  rng?: () => number;
}

const FUNDAMENTALS: FundamentalStat[] = ['VIT', 'ESP', 'END', 'FOR', 'DEX', 'INT', 'SAG', 'CHA', 'AGI'];
const ELEMENTS: ElementId[] = ['fire', 'ice', 'lightning', 'earth', 'wind', 'holy', 'dark', 'water', 'nature'];
const AFFINITIES = ELEMENTS.map((element) => `affinity_${element}` as const);
const RESISTANCES = ELEMENTS.map((element) => `resistance_${element}` as const);

const SLOT_POOLS: Record<EquipmentSlot, EquipmentAffixKey[]> = {
  hat: ['ESP', 'INT', 'SAG', 'CHA', ...AFFINITIES, ...RESISTANCES],
  accessory: ['ESP', 'INT', 'SAG', 'CHA', 'AGI', ...AFFINITIES, ...RESISTANCES],
  armor_top: ['VIT', 'END', 'FOR', 'SAG', ...RESISTANCES],
  armor_bottom: ['VIT', 'END', 'DEX', 'AGI', ...RESISTANCES],
  gloves: ['FOR', 'DEX', 'INT', 'SAG', 'CHA', ...AFFINITIES],
  shoes: ['VIT', 'DEX', 'AGI', ...RESISTANCES],
  weapon_right: [...FUNDAMENTALS, ...AFFINITIES],
  weapon_left: ['VIT', 'END', 'ESP', 'SAG', ...AFFINITIES, ...RESISTANCES],
  wings: ['VIT', 'ESP', 'CHA', 'AGI', ...AFFINITIES, ...RESISTANCES],
};

const FAMILY_POOLS: Record<string, EquipmentAffixKey[]> = {
  martial: ['FOR', 'VIT', 'END', 'DEX', ...AFFINITIES],
  arcane: ['INT', 'ESP', 'SAG', ...AFFINITIES],
  finesse: ['DEX', 'AGI', 'CHA', 'FOR', ...AFFINITIES],
  support: ['CHA', 'SAG', 'ESP', 'DEX', ...AFFINITIES, ...RESISTANCES],
  shield: ['VIT', 'END', 'ESP', 'SAG', ...RESISTANCES],
  accessory: ['ESP', 'INT', 'SAG', 'CHA', 'AGI', ...AFFINITIES, ...RESISTANCES],
  wings: ['VIT', 'ESP', 'CHA', 'AGI', ...AFFINITIES, ...RESISTANCES],
};

export function equipmentAffixPoolForFamily(
  family: string,
  slot: EquipmentSlot,
): EquipmentAffixKey[] {
  return [...(FAMILY_POOLS[family] || SLOT_POOLS[slot])];
}

export const RARITY_AFFIX_COUNT: Record<EquipmentRarity, number> = {
  common: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
};

const RARITY_BUDGET_MULTIPLIER: Record<EquipmentRarity, number> = {
  common: 1,
  rare: 1.15,
  epic: 1.35,
  legendary: 1.6,
};

const STAR_MULTIPLIER: Record<EquipmentQualityStars, number> = {
  1: 0.8,
  2: 0.9,
  3: 1,
  4: 1.1,
  5: 1.2,
};

function safeRandom(rng: () => number): number {
  const value = Number(rng());
  if (!Number.isFinite(value)) return 0;
  return Math.min(0.999999999, Math.max(0, value));
}

function drawQualityStars(rng: () => number): EquipmentQualityStars {
  const roll = safeRandom(rng);
  if (roll < 0.10) return 1;
  if (roll < 0.30) return 2;
  if (roll < 0.70) return 3;
  if (roll < 0.92) return 4;
  return 5;
}

function shuffledUnique<T>(values: readonly T[], count: number, rng: () => number): T[] {
  const copy = [...new Set(values)];
  for (let index = copy.length - 1; index > 0; index--) {
    const other = Math.floor(safeRandom(rng) * (index + 1));
    [copy[index], copy[other]] = [copy[other]!, copy[index]!];
  }
  return copy.slice(0, Math.max(0, count));
}

function totalAffixBudget(itemLevel: number, rarity: EquipmentRarity, stars: EquipmentQualityStars): number {
  const levelBudget = 1 + Math.max(1, itemLevel) * 0.22;
  return Math.max(
    RARITY_AFFIX_COUNT[rarity],
    Math.round(levelBudget * RARITY_BUDGET_MULTIPLIER[rarity] * STAR_MULTIPLIER[stars]),
  );
}

function distributeBudget(keys: EquipmentAffixKey[], budget: number, rng: () => number): EquipmentAffixRoll[] {
  if (!keys.length) return [];
  const weights = keys.map(() => 0.75 + safeRandom(rng) * 0.5);
  const sum = weights.reduce((total, value) => total + value, 0);
  const values = weights.map((weight) => Math.max(1, Math.floor((budget * weight) / sum)));
  let remaining = budget - values.reduce((total, value) => total + value, 0);
  let cursor = 0;
  while (remaining > 0) {
    values[cursor % values.length]! += 1;
    cursor++;
    remaining--;
  }
  return keys.map((key, index) => ({ key, value: values[index]! }));
}

export function generateEquipmentInstance(input: GenerateEquipmentInput): EquipmentInstance {
  if (!input.instanceId) throw new Error('instanceId requis');
  if (!input.ownerId) throw new Error('ownerId requis');
  if (!input.template?.id) throw new Error('modèle d’équipement requis');

  const rng = input.rng || Math.random;
  const itemLevel = Math.max(1, Math.min(PLAYER_LEVEL_MAX, Math.floor(Number(input.itemLevel) || 1)));
  const qualityStars = drawQualityStars(rng);
  const desiredCount = RARITY_AFFIX_COUNT[input.rarity];
  const pool = input.template.allowedAffixes?.length
    ? input.template.allowedAffixes
    : SLOT_POOLS[input.template.slot];
  const keys = shuffledUnique(pool, Math.min(desiredCount, pool.length), rng);
  const budget = totalAffixBudget(itemLevel, input.rarity, qualityStars);

  return {
    instanceId: input.instanceId,
    templateId: input.template.id,
    ownerId: input.ownerId,
    slot: input.template.slot,
    itemLevel,
    rarity: input.rarity,
    qualityStars,
    affixes: distributeBudget(keys, budget, rng),
    requiredCertification: certificationForItemLevel(itemLevel),
    bound: true,
    source: { ...input.source },
    generatedAt: input.generatedAt ?? Date.now(),
  };
}

export function equipmentInstanceStats(instance: EquipmentInstance): Record<string, number> {
  const stats = { ...(instance.legacyStats || {}) };
  return instance.affixes.reduce<Record<string, number>>((stats, affix) => {
    stats[affix.key] = (stats[affix.key] || 0) + affix.value;
    return stats;
  }, stats);
}

/** RNG déterministe pour tests, reçus et reproduction d'un incident. */
export function createSeededEquipmentRng(seed: string): () => number {
  let state = 2166136261;
  for (let index = 0; index < seed.length; index++) {
    state ^= seed.charCodeAt(index);
    state = Math.imul(state, 16777619);
  }
  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
